import React from 'react'
import Table from 'react-bootstrap/Table'
import { useRouteContext } from '@tanstack/react-router'
import {
    ColumnDef,
    ColumnFiltersState,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    Row,
    RowData,
    TableOptions,
    useReactTable,
} from '@tanstack/react-table'

import { PreschoolModel, SCLR, ENRL, CLEN, SLNG, LeaDataModel, defaultLeaData, CoreDataModel } from '@/models'
import { FAMI } from '@/models/fami'
import { CHLD } from '@/models/chld'
import { STFF} from '@/models/stff'
import { CLRM } from '@/models/clrm'
import { FileStatus, FileSubmission } from '@/models/file-submission.tsx'
import { User, UserOrg, UserOrgForm, userMutationFunctions } from '@/models/user.tsx'

export function Filter({ column }) {
    const { filterVariant } = column.columnDef.meta ?? {}

    const columnFilterValue = column.getFilterValue()

    const sortedUniqueValues = React.useMemo(
        () =>
            filterVariant === 'range'
                ? []
                : Array.from(column.getFacetedUniqueValues().keys())
                    .sort()
                    .slice(0, 5000),
        [column.getFacetedUniqueValues(), filterVariant]
    )

    return filterVariant === 'range' ? (
        <div>
            <div className="flex space-x-2">
                <DebouncedInput
                    type="number"
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    initialValue={(columnFilterValue)?.[0] ?? ''}
                    onChange={value =>
                        column.setFilterValue((old) => [value, old?.[1]])
                    }
                    placeholder={`Min ${column.getFacetedMinMaxValues()?.[0] !== undefined
                            ? `(${column.getFacetedMinMaxValues()?.[0]})`
                            : ''
                        }`}
                    className="w-24 border shadow rounded"
                />
                <DebouncedInput
                    type="number"
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    initialValue={(columnFilterValue)?.[1] ?? ''}
                    onChange={value =>
                        column.setFilterValue((old) => [old?.[0], value])
                    }
                    placeholder={`Max ${column.getFacetedMinMaxValues()?.[1]
                            ? `(${column.getFacetedMinMaxValues()?.[1]})`
                            : ''
                        }`}
                    className="w-24 border shadow rounded"
                />
            </div>
            <div className="h-1" />
        </div>
    ) : filterVariant === 'select' ? (
        <select
            className="w-36 width-100pct border shadow rounded"
            onChange={e => column.setFilterValue(e.target.value)}
            value={columnFilterValue?.toString()}
        >
            <option value="">All</option>
            {sortedUniqueValues.map(value => (
                //dynamically generated select options from faceted values feature
                <option value={value} key={value}>
                    {value}
                </option>
            ))}
        </select>
    ) : (
        <>
            {/* Autocomplete suggestions from faceted values feature */}
            <datalist id={column.id + 'list'}>
                {sortedUniqueValues.map((value) => (
                    <option value={value} key={value} />
                ))}
            </datalist>
            <DebouncedInput
                type="text"
                initialValue={(columnFilterValue ?? '')}
                onChange={value => column.setFilterValue(value)}
                placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
                className="w-36 width-100pct border shadow rounded"
                list={column.id + 'list'}
            />
            <div className="h-1" />
        </>
    )
}

// A typical debounced input react component
function DebouncedInput({
    initialValue,
    onChange,
    debounce = 500,
    ...props
}) {
    const [value, setValue] = React.useState(initialValue)

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
    )
}

export function defaultTableOptions<TData extends RowData>(): Omit<TableOptions<TData>, 'data' | 'columns'> {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        []
    )
    return {
        state: {
            columnFilters,
        },
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(), //client-side filtering
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(), // client-side faceting
        getFacetedUniqueValues: getFacetedUniqueValues(), // generate unique values for select filter/autocomplete
        getFacetedMinMaxValues: getFacetedMinMaxValues(), // generate min/max values for range filter
        debugTable: true,
        debugHeaders: true,
        debugColumns: false,
    }
}

export function tableObject<TData extends RowData>(options: TableOptions<TData>, rowClickFn: ((row: Row<TData>) => void) | false = false) {
    const table = useReactTable<TData>(options)
    return {
        table: table,
        render: () => {
            return (
                <div>
                    <Table responsive>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        return (
                                            <th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                aria-sort={
                                                    header.column.getCanSort() &&
                                                    header.column.getIsSorted() &&
                                                    header.column.getSortIndex() === 0
                                                        ? header.column.getIsSorted() === 'asc'
                                                            ? 'ascending'
                                                            : 'descending'
                                                        : undefined
                                                }
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <>
                                                        {header.column.getCanSort() ? (
                                                            <button
                                                                type="button"
                                                                className="button-no-styles select-none"
                                                                onClick={header.column.getToggleSortingHandler()}
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                                <span aria-hidden="true">
                                                                    {{
                                                                        asc: ' 🔼',
                                                                        desc: ' 🔽',
                                                                    }[header.column.getIsSorted() as string] ?? null}
                                                                </span>
                                                            </button>
                                                        ) : (
                                                            <div>
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </div>
                                                        )}
                                                        {header.column.getCanFilter() ? (
                                                            <div>
                                                                <Filter column={header.column} />
                                                            </div>
                                                        ) : null}
                                                    </>
                                                )}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => {
                                return (
                                    <tr key={row.id} onClick={rowClickFn ? () => { rowClickFn(row) } : undefined }>
                                        {row.getVisibleCells().map(cell => {
                                            return (
                                                <td key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                    <div className="h-2" />
                    {table.getPageCount() > 1 &&
                        <div className="flex items-center gap-2">
                            <button className="border rounded p-1" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                                {'<<'}
                            </button>
                            <button className="border rounded p-1" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                                {'<'}
                            </button>
                            <button className="border rounded p-1" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                                {'>'}
                            </button>
                            <button className="border rounded p-1" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
                                {'>>'}
                            </button>
                            <span className="flex items-center gap-1">
                                <div>Page</div>
                                <strong>
                                    {table.getState().pagination.pageIndex + 1} of{' '}
                                    {table.getPageCount()}
                                </strong>
                            </span>
                            <span className="flex items-center gap-1">
                                | Go to page:
                                <input
                                    type="number"
                                    defaultValue={table.getState().pagination.pageIndex + 1}
                                    onChange={e => { const page = e.target.value ? Number(e.target.value) - 1 : 0; table.setPageIndex(page) }}
                                    className="border p-1 rounded w-16"
                                />
                            </span>
                            <select value={table.getState().pagination.pageSize} onChange={e => { table.setPageSize(Number(e.target.value)) }}>
                                {[10, 20, 30, 40, 50].map(pageSize => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ))}
                            </select>
                        </div>
                    }
                    {/*<div>{table.getPrePaginationRowModel().rows.length} Rows</div>*/}
                    {/*<div>*/}
                    {/*    <button onClick={() => rerender()}>Force Rerender</button>*/}
                    {/*</div>*/}
                    {/*  <pre>*/}
                    {/*    {JSON.stringify(*/}
                    {/*      { columnFilters: table.getState().columnFilters },*/}
                    {/*      null,*/}
                    {/*      2*/}
                    {/*    )}*/}
                    {/*  </pre>*/}
                </div>
            )
        }
    }
}

export function CAPSDACTable<T>({ data, columns, rowClickFn = false }: { data: T[], columns: ColumnDef<T, any>[], rowClickFn: ((row: Row<T>) => void) | false }) {
    var table = tableObject({ ...{ data: data, columns: columns }, ...defaultTableOptions<T>() }, rowClickFn)
    return table.render()
}

export function useChildTableColumns(old_routeContext: null, leaData: LeaDataModel, selectRow: (value: CHLD | undefined) => void): ColumnDef<CHLD, any>[] {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const columnHelper = createColumnHelper<CHLD>()
    return React.useMemo<ColumnDef<CHLD, any>[]>(
        () => [
            {
                id: 'preschoolCDSCode',
                accessorFn: row => {
                    const x = row.Enrollments.map(e => leaContext.currentOrgPreschools.find(x => x.PreschoolCDSCode === e.PreschoolCode)?.PreschoolName)
                    if (x.length == 1)
                        return x[0]
                    return x.length
                },
                header: () => <span>Preschool</span>,
                filterFn: (row: Row<CHLD>, columnId: string, filterValue: any) => {
                    if (typeof (filterValue) === "string") {
                        return (row.original.Enrollments.find(e => leaContext.currentOrgPreschools.find(x => x.PreschoolCDSCode === e.PreschoolCode)?.PreschoolName.includes(filterValue)) !== undefined)
                    }
                    return row.original.Enrollments.length == filterValue
                },
                cell: (props) => (typeof (props.getValue()) === "number") ? `${props.getValue()} enrollments` : props.getValue(),
            },
            {
                id: 'childName',
                accessorFn: row => `${row.ChildLastName}, ${row.ChildFirstName} ${row.ChildMiddleName}`,
                cell: info => info.getValue(),
                header: () => <span>Child Name<br />(Last, First MI)</span>,
            },
            {
                id: 'CSPPID',
                accessorFn: row => row.CSPPID,
                cell: info => info.getValue(),
                header: () => <span>CSPPID</span>,
            },
            {
                id: 'childId',
                accessorFn: row => row.LocalChildId,
                cell: info => info.getValue(),
                header: () => <span>Child ID</span>,
            },
            {
                id: 'ChildDateOfBirth',
                accessorFn: row => row.ChildDateOfBirth,
                cell: info => info.getValue(),
                header: () => <span>Date of Birth</span>,
            },
            {
                id: 'EffectiveStartDate',
                accessorFn: row => row.EffectiveStartDate,
                cell: info => info.getValue(),
                header: () => <span>Effective Date</span>,
            },
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original)}>
                            <span className="sr-only">Edit this Child Record</span>
                            <i title="Edit this Child Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Child Record">
                            Edit
                        </a>
                    </div>
            }),
            //  {
            //    accessorKey: 'visits',
            //    header: () => <span>Visits</span>,
            //    meta: {
            //      filterVariant: 'range',
            //    },
            //  },
        ],
        [old_routeContext]
    )
}

export function useFamiTableColumns(old_routeContext: null, selectRow: (value: FAMI | undefined) => void, excludeColumns: string[] = []): ColumnDef<FAMI, any>[] {
    const columnHelper = createColumnHelper<FAMI>()
    const columns: ColumnDef<FAMI, any>[] = [
        //{
        //    id: 'preschoolCDSCode',
        //    accessorFn: row => {
        //        const x = row.Enrollments.map(e => leaData.preschools.find(x => x.PreschoolCDSCode === e.PreschoolCode)?.PreschoolName)
        //        if (x.length == 1)
        //            return x[0]
        //        return x.length
        //    },
        //    header: () => <span>Preschool</span>,
        //    filterFn: (row: Row<FAMI>, columnId: string, filterValue: any) => {
        //        if (typeof (filterValue) === "string") {
        //            return (row.original.Enrollments.find(e => leaData.preschools.find(x => x.PreschoolCDSCode === e.PreschoolCode)?.PreschoolName.includes(filterValue)) !== undefined)
        //        }
        //        return row.original.Enrollments.length == filterValue
        //    },
        //    cell: (props) => (typeof (props.getValue()) === "number") ? `${props.getValue()} enrollments` : props.getValue(),
        //},
        {
            id: 'headOfHousehold1Name',
            accessorFn: row => `${row.HeadOfHousehold1LastName}, ${row.HeadOfHousehold1FirstName} ${row.HeadOfHousehold1MiddleName}`,
            cell: info => info.getValue(),
            header: () => <span>Head of Household 1 Name<br />(Last, First MI)</span>,
        },
        {
            id: 'headOfHousehold2Name',
            accessorFn: row => row.HeadOfHousehold2LastName ? `${row.HeadOfHousehold2LastName}, ${row.HeadOfHousehold2FirstName} ${row.HeadOfHousehold2MiddleName}` : '',
            cell: info => info.getValue(),
            header: () => <span>Head of Household 2 Name<br />(Last, First MI)</span>,
        },
        {
            accessorFn: row => row.LocalFamilyId,
            id: 'familyId',
            cell: info => info.getValue(),
            header: () => <span>Family ID</span>,
        },
        {
            accessorFn: row => row.CSPPID,
            id: 'CSPPID',
            cell: info => info.getValue(),
            header: () => <span>CSPPID</span>,
        },
        {
            accessorFn: row => row.EffectiveStartDate,
            id: 'EffectiveStartDate',
            cell: info => info.getValue(),
            header: () => <span>Effective Date</span>,
        },
        columnHelper.display({
            id: 'edit',
            header: () => 'Edit',
            cell: props =>
                <div>
                    <a onClick={() => selectRow(props.row.original)}>
                        <span className="sr-only">Edit this Family Record</span>
                        <i title="Edit this Family Record" className="fa fa-edit"></i>
                    </a>
                    <br />
                    <a onClick={() => selectRow(props.row.original)} title="Edit this Family Record">
                        Edit
                    </a>
                </div>
        }),
    ]
    return React.useMemo<ColumnDef<FAMI, any>[]>(
        () => columns.filter(x => x.id === undefined || excludeColumns.find(y => y === x.id) === undefined),
        [old_routeContext]
    )
}


export function useStaffTableColumns(old_routeContext: null, coreData: CoreDataModel, leaData: LeaDataModel, selectRow: (value: STFF | undefined) => void): ColumnDef<STFF, any>[] {
    const columnHelper = createColumnHelper<STFF>()
    return React.useMemo<ColumnDef<STFF, any>[]>(
        () => [
            //{
            //    id: 'preschoolCDSCode',
            //    accessorFn: row => leaData.preschools.find(x => x.PreschoolCDSCode === row.PreschoolCode)?.PreschoolName,
            //    cell: info => info.getValue(),
            //    header: () => <span>Preschool</span>,
            //        accessorFn: row => {
            //            const x = row.ClassroomAssignments.map(e => preschools.find(x => x.PreschoolCDSCode === e.PreschoolCode)?.PreschoolName)
            //            if (x.length == 1)
            //                return x[0]
            //            return x.length
            //        },
            //        header: () => <span>Preschool</span>,
            //        filterFn: (row: Row<STAFF>, columnId: string, filterValue: any) => {
            //            if (typeof (filterValue) === "string") {
            //                return (row.original.ClassroomAssignments.find(e => preschools.find(x => x.PreschoolCDSCode === e.PreschoolCode)?.PreschoolName.includes(filterValue)) !== undefined)
            //            }
            //            return row.original.ClassroomAssignments.length == filterValue
            //        },
            //        cell: (props) => (typeof (props.getValue()) === "number") ? `${props.getValue()} classrooms` : props.getValue(),
            //},
            {
                id: 'staffName',
                accessorFn: row => `${row.StaffLegalLastName}, ${row.StaffLegalFirstName} ${row.StaffLegalMiddleName}`,
                cell: info => info.getValue(),
                header: () => <span>Staff Name (Last, First MI)</span>,
            },
            {
                accessorFn: row => row.LocalStaffId,
                id: 'staffId',
                cell: info => info.getValue(),
                header: () => <span>Staff ID</span>,
            },
            //{
            //    id: 'StaffRoleName',
            //    accessorFn: row => {
            //        const x = [... new Set(row.ClassroomAssignments.map(e => coreData.staffRoleList.find(x => x.Value === e.StaffClassroomRole)?.Text))]
            //        if (x.length == 1)
            //            return x[0]
            //        else if (x.length == 0)
            //            return ""
            //        return x.length
            //    },
            //    header: () => <span>Role</span>,
            //    filterFn: (row: Row<STAFF>, columnId: string, filterValue: any) => {
            //        if (typeof (filterValue) === "string") {
            //            return (row.original.ClassroomAssignments.find(e => coreData.staffRoleList.find(x => x.Value === e.StaffClassroomRole)?.Text.includes(filterValue)) !== undefined)
            //        }
            //        return row.original.ClassroomAssignments.length == filterValue
            //    },
            //    cell: (props) => (typeof (props.getValue()) === "number") ? `${props.getValue()} roles` : props.getValue(),
            //},
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original)}>
                            <span className="sr-only">Edit this Staff Record</span>
                            <i title="Edit this Staff Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Staff Record">
                            Edit
                        </a>
                    </div>
            }),
        ],
        [old_routeContext]
    )
}

export function useClassroomTableColumns(old_routeContext: null, leaData: LeaDataModel, selectRow: (value: CLRM | undefined) => void): ColumnDef<CLRM, any>[] {
    const leaContext = useRouteContext({ from: '/$leaid' })
    const columnHelper = createColumnHelper<CLRM>()
    return React.useMemo<ColumnDef<CLRM, any>[]>(
        () => [
            {
                id: 'PreschoolCDSCode',
                accessorFn: row => leaContext.currentOrgPreschools.find(x => x.PreschoolCDSCode === row.PreschoolCode)?.PreschoolName,
                cell: info => info.getValue(),
                header: () => <span>Preschool</span>,
            },
            {
                accessorKey: 'ClassroomName',
                cell: info => info.getValue(),
                header: () => <span>Classroom Name</span>,
            },
            {
                accessorFn: row => row.LocalClassroomId,
                id: 'ClassroomId',
                cell: info => info.getValue(),
                header: () => <span>ClassroomID</span>,
            },
            {
                id: 'enrollmentCount',
                accessorFn: row => row.ChildAssignments.length,
                cell: info => info.getValue(),
                header: () => <span>Enrollment</span>,
            },
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original) }>
                            <span className="sr-only">Edit this Classroom Record</span>
                            <i title="Edit this Classroom Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Classroom Record">
                            Edit
                        </a>
                    </div>
            }),
            //  {
            //    accessorKey: 'visits',
            //    header: () => <span>Visits</span>,
            //    meta: {
            //      filterVariant: 'range',
            //    },
            //  },
        ],
        [old_routeContext]
    )
}

export function useSclrTableColumns(old_routeContext: null, leaData: LeaDataModel, selectRow: (value: SCLR | undefined) => void): ColumnDef<SCLR, any>[] {
    const columnHelper = createColumnHelper<SCLR>()
    return React.useMemo<ColumnDef<SCLR, any>[]>(
        () => [
            {
                accessorFn: row => row.LocalClassroomId,
                id: 'ClassroomId',
                cell: info => info.getValue(),
                header: () => <span>ClassroomID</span>,
            },
            {
                accessorKey: 'ClassroomName',
                accessorFn: row => leaData.clrm.find(x => x.LocalClassroomId === row.LocalClassroomId)?.ClassroomName,
                cell: info => info.getValue(),
                header: () => <span>Classroom Name</span>,
            },
            {
                accessorKey: 'StaffName',
                accessorFn: row => {
                    var staff = leaData.stff.find(x => x.LocalStaffId === row.LocalStaffId)
                    return `${staff?.StaffLegalLastName} ${staff?.StaffLegalFirstName} (${row.LocalStaffId})`
                },
                cell: info => info.getValue(),
                header: () => <span>Staff Name</span>,
            },
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original)}>
                            <span className="sr-only">Edit this Classroom Record</span>
                            <i title="Edit this Classroom Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Classroom Record">
                            Edit
                        </a>
                    </div>
            }),
        ],
        [old_routeContext]
    )
}

export function useEnrlTableColumns(old_routeContext: null, preschools: PreschoolModel[], selectRow: (value: ENRL | undefined) => void): ColumnDef<ENRL, any>[] {
    const columnHelper = createColumnHelper<ENRL>()
    return React.useMemo<ColumnDef<ENRL, any>[]>(
        () => [
            {
                id: 'PreschoolCDSCode',
                accessorFn: row => preschools.find(x => x.PreschoolCDSCode === row.PreschoolCode)?.PreschoolName,
                cell: info => info.getValue(),
                header: () => <span>Preschool</span>,
            },
            {
                id: 'EnrollmentStartDate',
                accessorFn: row => row.EnrollmentStartDate,
                cell: info => info.getValue(),
                header: () => <span>Enrollment Start</span>,
            },
            {
                id: 'EnrollmentEndDate',
                accessorFn: row => row.EnrollmentEndDate,
                cell: info => info.getValue(),
                header: () => <span>Enrollment End</span>,
            },
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original)}>
                            <span className="sr-only">Edit this Enrollment Record</span>
                            <i title="Edit this Classroom Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Enrollment Record">
                            Edit
                        </a>
                    </div>
            }),
        ],
        [old_routeContext]
    )
}

export function useClenTableColumns(old_routeContext: null, leaData: LeaDataModel, selectRow: (value: CLEN | undefined) => void): ColumnDef<CLEN, any>[] {
    const columnHelper = createColumnHelper<CLEN>()
    return React.useMemo<ColumnDef<CLEN, any>[]>(
        () => [
            {
                accessorFn: row => row.LocalClassroomId,
                id: 'ClassroomId',
                cell: info => info.getValue(),
                header: () => <span>ClassroomID</span>,
            },
            {
                accessorKey: 'ClassroomName',
                accessorFn: row => leaData.clrm.find(x => x.LocalClassroomId === row.LocalClassroomId)?.ClassroomName,
                cell: info => info.getValue(),
                header: () => <span>Classroom Name</span>,
            },
            {
                accessorKey: 'ChildName',
                accessorFn: row => {
                    var child = leaData.chld.find(x => x.CSPPID === row.CSPPID)
                    return `${child?.ChildLastName} ${child?.ChildFirstName} (${row.CSPPID})`
                },
                cell: info => info.getValue(),
                header: () => <span>Child Name</span>,
            },
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original)}>
                            <span className="sr-only">Edit this Classroom Record</span>
                            <i title="Edit this Classroom Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Classroom Record">
                            Edit
                        </a>
                    </div>
            }),
        ],
        [old_routeContext]
    )
}

export function useSlngTableColumns(old_routeContext: null, selectRow: (value: SLNG | undefined) => void): ColumnDef<SLNG, any>[] {
    const columnHelper = createColumnHelper<SLNG>()
    return React.useMemo<ColumnDef<SLNG, any>[]>(
        () => [
            {
                accessorFn: row => row.InterviewHomeLanguage1,
                id: 'InterviewHomeLanguage1',
                cell: info => info.getValue(),
                header: () => <span>Interview Home Language 1</span>,
            },
            {
                accessorFn: row => row.InterviewMostUsedLanguage,
                id: 'InterviewMostUsedLanguage',
                cell: info => info.getValue(),
                header: () => <span>Interview Most Used Language</span>,
            },
            columnHelper.display({
                id: 'edit',
                header: () => 'Edit',
                cell: props =>
                    <div>
                        <a onClick={() => selectRow(props.row.original)}>
                            <span className="sr-only">Edit this Supplemental DLL Record</span>
                            <i title="Edit this Supplemental DLL Record" className="fa fa-edit"></i>
                        </a>
                        <br />
                        <a onClick={() => selectRow(props.row.original)} title="Edit this Supplemental DLL Record">
                            Edit
                        </a>
                    </div>
            }),
        ],
        [old_routeContext]
    )
}

export function useUserTableColumns(): ColumnDef<User<UserOrg>, any>[] {
    return React.useMemo<ColumnDef<User<UserOrg>, any>[]>(
        () => [
            {
                accessorKey: 'firstName',
                header: () => <span>First Name</span>,
            },
            {
                accessorKey: 'lastName',
                header: () => <span>Last Name</span>,
            },
            {
                accessorKey: 'userName',
                header: () => <span>Username</span>,
            },
            {
                accessorKey: 'phoneNumber',
                header: () => <span>Phone Number</span>,
            },
        ],
        []
    )
}

export function userOrgTableColumns(coreData: CoreDataModel): ColumnDef<UserOrg, any>[] {
    return React.useMemo<ColumnDef<UserOrg, any>[]>(
        () => [
            {
                id: 'orgType',
                accessorFn: row => {
                    return coreData.securityGroup.find(x => x.ScrtyGrpKey === row.orgType)?.ScrtyGrpName
                },
                header: () => <span>Org Type</span>,
            },
            {
                accessorKey: 'orgName',
                header: () => <span>Org Name</span>,
            },
            {
                id: 'roles',
                accessorFn: row => {
                    return row.roleIds.map(e => coreData.securityRoles.find(x => x.CAPSDACRoleId === e)?.CAPSDACRoleName).join(', ')
                },
                filterFn: (row: Row<UserOrg>, columnId: string, filterValue: any) => {
                    return (row.original.roleIds.map(e => coreData.securityRoles.find(x => x.CAPSDACRoleId === e)).find(e => e?.CAPSDACRoleName.includes(filterValue)) !== undefined)
                },
                header: () => <span>Roles</span>,
            },
        ],
        []
    )
}

export function useFileSubmissionTableColumns(excludeColumns: string[] = []): ColumnDef<FileSubmission, any>[] {
    const columns = [
        {
            id: 'Id',
            accessorKey: 'Id',
            header: () => <span>Id</span>,
        },
        {
            id: 'CreateDate',
            accessorFn: row => { return new Date(row.CreateDate) },
            cell: info =>
                <div>
                    <span style={{ whiteSpace: "nowrap" }}>{info.getValue().toLocaleDateString()}</span>{" "}
                    <span style={{ whiteSpace: "nowrap" }}>{info.getValue().toLocaleTimeString()}</span>
                </div>
            ,
            header: () => <span>Create Date</span>,
        },
        {
            id: 'CreatedByUserId',
            accessorKey: 'CreatedByUserId',
            header: () => <span>Submitter</span>,
        },
        {
            id: 'ClientFileName',
            accessorKey: 'ClientFileName',
            header: () => <span>Filename</span>,
        },
        {
            id: 'CurrentStatus',
            accessorFn: row => FileStatus.get(row.CurrentStatus.toString())?.Text,
            header: () => <span>Status</span>,
        },
        {
            id: 'RecordTypeCode',
            accessorKey: 'RecordTypeCode',
            header: () => <span>Record Type</span>,
        },
        {
            id: 'Errors',
            accessorKey: 'Errors',
            header: () => <span>Errors</span>,
        },
        {
            id: 'InputRecords',
            accessorKey: 'InputRecords',
            header: () => <span># Records</span>,
        },
    ]
    return React.useMemo<ColumnDef<FileSubmission, any>[]>(
        () => columns.filter(x => x.id === undefined || excludeColumns.find(y => y === x.id) === undefined),
        []
    )
}